<script lang="ts">
  import { onMount } from 'svelte';

  type ViewType = 'year' | 'month';

  type Props = {
    selectedPeriod: string;
    onPeriodChange: (period: string) => void;
  };

  let { selectedPeriod, onPeriodChange }: Props = $props();

  // Parse current selection
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Date range from API
  let minYear = $state(currentYear - 4);
  let maxYear = $state(currentYear);

  // Determine view type and selected values from period
  let viewType = $state<ViewType>('month');
  let selectedYear = $state(currentYear);
  let selectedMonth = $state(currentMonth); // 0-11

  // Load available date range from transactions in storage provider
  onMount(async () => {
    try {
      const { getTransactions } = await import('../../lib/storage.js');
      const transactions = await getTransactions();

      if (transactions.length > 0) {
        const dates = transactions.map(t => new Date(t.date));
        const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

        minYear = minDate.getFullYear();
        maxYear = maxDate.getFullYear();
      }

    } catch (error) {
      console.error('Failed to load date range:', error);
    }
  });

  // Parse selectedPeriod to set initial state
  $effect(() => {
    if (selectedPeriod) {
      if (selectedPeriod.length === 4) {
        // YYYY format
        viewType = 'year';
        selectedYear = parseInt(selectedPeriod);
      } else if (selectedPeriod.includes('-')) {
        // YYYY-MM format
        viewType = 'month';
        const [year, month] = selectedPeriod.split('-');
        selectedYear = parseInt(year);
        selectedMonth = parseInt(month) - 1; // Convert to 0-based
      }
    }
  });

  // Available years (from minYear to maxYear based on transaction data)
  const years = $derived(() => {
    const yearsList = [];
    for (let year = maxYear; year >= minYear; year--) {
      yearsList.push(year);
    }
    return yearsList;
  });

  // Month names
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Handle view type change
  const handleViewTypeChange = (type: ViewType) => {
    viewType = type;
    updatePeriod();
  };

  // Handle year change
  const handleYearChange = (year: number) => {
    selectedYear = year;
    updatePeriod();
  };

  // Handle month change
  const handleMonthChange = (month: number) => {
    selectedMonth = month;
    updatePeriod();
  };

  // Update period string and notify parent
  const updatePeriod = () => {
    let period = '';
    if (viewType === 'year') {
      period = selectedYear.toString();
    } else {
      // Format: YYYY-MM
      period = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    }
    onPeriodChange(period);
  };

  // Determine if month/year is in future
  const isInFuture = (year: number, month?: number) => {
    if (month !== undefined) {
      const date = new Date(year, month);
      return date > currentDate;
    }
    return year > currentYear;
  };
</script>

<div class="month-year-selector">
  <!-- View Type Tabs -->
  <div class="view-type-tabs">
    <button
      class="tab-btn"
      class:active={viewType === 'year'}
      onclick={() => handleViewTypeChange('year')}
    >
      This Year
    </button>
    <button
      class="tab-btn"
      class:active={viewType === 'month'}
      onclick={() => handleViewTypeChange('month')}
    >
      All Time
    </button>
  </div>

  <!-- Year Pills -->
  <div class="year-pills">
    {#each years() as year}
      <button
        class="year-pill"
        class:active={selectedYear === year}
        class:future={isInFuture(year)}
        disabled={isInFuture(year)}
        onclick={() => handleYearChange(year)}
      >
        {year}
      </button>
    {/each}
  </div>

  <!-- Month Pills (only shown for month view) -->
  {#if viewType === 'month'}
    <div class="month-pills">
      {#each monthNames as monthName, index}
        <button
          class="month-pill"
          class:active={selectedMonth === index && viewType === 'month'}
          class:future={isInFuture(selectedYear, index)}
          disabled={isInFuture(selectedYear, index)}
          onclick={() => handleMonthChange(index)}
        >
          {monthName}
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .month-year-selector {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
    padding: var(--spacing-lg);
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
  }

  .view-type-tabs {
    display: flex;
    gap: var(--spacing-xs);
    align-self: flex-end;
  }

  .tab-btn {
    padding: var(--spacing-xs) var(--spacing-md);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .tab-btn:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .tab-btn.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }

  .year-pills {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
  }

  .year-pill {
    padding: var(--spacing-xs) var(--spacing-lg);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    min-width: 70px;
  }

  .year-pill:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    transform: translateY(-1px);
  }

  .year-pill.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
    font-weight: var(--font-weight-semibold);
  }

  .year-pill.future {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .month-pills {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
  }

  .month-pill {
    padding: var(--spacing-xs) var(--spacing-md);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    min-width: 50px;
  }

  .month-pill:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    transform: translateY(-1px);
  }

  .month-pill.active {
    background: var(--color-success);
    border-color: var(--color-success);
    color: white;
    font-weight: var(--font-weight-semibold);
  }

  .month-pill.future {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    .month-year-selector {
      padding: var(--spacing-md);
    }

    .month-pills,
    .year-pills {
      justify-content: center;
    }
  }
</style>
